import { Button, Loader } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import { formatted } from "src/billinglatest/util/currency";
import darkPixiIcon from "src/pages/Welcome/images/dark-pixi-icon.svg";
import { resolveEffectiveShipping } from "../../effectiveShipping";
import {
  emptyPixiAddress,
  type PixiWizardSelection,
} from "../../hooks/usePixiWizardState";
import { useCheckoutEstimate } from "../CheckoutStep/useCheckoutEstimate";
import { useCheckoutPricing } from "../CheckoutStep/useCheckoutPricing";
import { maskEmail } from "./utils/maskEmail";
import {
  CompleteButtonRow,
  CompleteLayout,
  Divider,
  FreeDeviceLabel,
  Header,
  Heading,
  LicenseValue,
  PixiInlineLogo,
  PixiSavings,
  RenewSubtext,
  RowLabel,
  RowValue,
  SubTitle,
  SuccessIcon,
  SummaryCard,
  SummaryRow,
} from "./styles";

export interface CompleteStepProps {
  state: PixiWizardSelection;
  /** Exits the wizard (closes the modal). */
  onComplete: () => void;
}

/**
 * The post-purchase success screen (GRWBKK-1663, redesigned in GRWBKK-1729).
 * Renders inside the wizard modal in place of Checkout once the purchase
 * succeeds, as a single "order summary" card that matches the Figma success
 * state. Values come from the shared checkout pricing (repoint to the
 * customer-create response when that call lands).
 *
 * The summary shows the license count and the tax-inclusive total that is
 * actually charged — the checkout estimate `total` (licence + shipping + tax),
 * falling back to the client-computed grand total until the estimate resolves /
 * when there is no billing country, so an amount is always shown. This mirrors
 * the tax-inclusive total the Checkout step already computed (GRWBKK-1820). The
 * free-PIXI deal is a single line — `$0` with the device value as a saving —
 * NOT a calculated device-cost + discount + shipping breakdown (GRWBKK-1729
 * reverses the GRWBKK-1672 itemisation). The discount deal folds its saving into
 * the license price and hides the PIXI line entirely.
 *
 * The tax-inclusive total is reused from `state.purchasedTotalCents`, captured
 * on the inline card/invoice success — no re-fetch, so the amount is exactly
 * what was charged and there is no pre-tax flash. Only the redirect-resume path
 * (PayPal/Direct Debit), where the app reloaded and that in-memory value is
 * gone, re-derives it from the estimate (with a brief loader).
 */
export const CompleteStep = ({ state, onComplete }: CompleteStepProps) => {
  const pricing = useCheckoutPricing(state);
  const {
    planContent,
    currency,
    billingCycle,
    licenses,
    isFreeDevice,
    isDiscount,
    grandTotalCents,
    deviceSavingsCents,
  } = pricing;

  const price = (cents: number) => formatted(currency, cents);
  const billingAddress = state.billingAddress ?? emptyPixiAddress();
  // Mirror the Checkout step's shipping derivation (GRWBKK-1735) so the estimate
  // request is identical and returns the same tax-inclusive total.
  const shippingAddress = resolveEffectiveShipping({
    needsShipping: !isDiscount,
    useSameAddress: state.useSameAddressForShipping ?? false,
    billing: billingAddress,
    manualShipping: state.shippingAddress ?? emptyPixiAddress(),
  });

  // Prefer the total captured at purchase (the amount actually charged) — the
  // common inline path. Only when it's absent (redirect-resume: the app reloaded
  // and lost the in-memory value) do we re-fetch the estimate, so the inline
  // path issues no redundant request and never flashes the pre-tax fallback.
  const reusedTotalCents = state.purchasedTotalCents;
  const hasReusedTotal = reusedTotalCents !== undefined;

  // Tax comes from the backend estimate, never computed on the client
  // (GRWBKK-1820/1745/1744). Fall back to the client grand total when there's no
  // billing country (no estimate is coming) or the estimate errored, so the
  // summary always shows an amount.
  const { totalCents, error: estimateError } = useCheckoutEstimate({
    pricing,
    billingAddress,
    shippingAddress,
    vatNumber: state.vatNumber,
    enabled: !hasReusedTotal,
  });
  const displayTotalCents = reusedTotalCents ?? totalCents ?? grandTotalCents;

  // While a re-derived estimate is genuinely in flight (redirect-resume only),
  // show a loader instead of the client fallback — otherwise the pre-tax
  // fallback flashes for a frame before the tax-inclusive total resolves
  // (GRWBKK-1820). Derived synchronously from the billing country so the very
  // first render already shows the loader (the hook's `loading` flag only flips
  // after the mount effect runs). When the total is reused, or there's no
  // billing country, nothing is pending — show the amount immediately.
  const hasBillingCountry = Boolean(billingAddress.country);
  const estimatePending =
    !hasReusedTotal &&
    hasBillingCountry &&
    totalCents === undefined &&
    !estimateError;

  const maskedEmail = maskEmail(billingAddress.email);
  const isYearly = billingCycle === "year";

  return (
    <CompleteLayout data-testid="complete-step">
      <Header>
        <SuccessIcon
          name="checked-circle-fill"
          aria-hidden
          data-testid="complete-success-icon"
        />
        <Heading>
          <FormattedMessage
            id="pixi_wizard.complete.title"
            defaultMessage="You're officially a {planName} member!"
            values={{ planName: planContent.name.toLowerCase() }}
          />
        </Heading>
        <SubTitle data-testid="complete-subtitle">
          <FormattedMessage
            id="pixi_wizard.complete.subtitle"
            defaultMessage="Thank you for your subscription. Invoice and confirmation email has been sent to your email {email}"
            values={{ email: maskedEmail }}
          />
        </SubTitle>
      </Header>

      <SummaryCard data-testid="complete-summary">
        <SummaryRow>
          <RowLabel>
            <FormattedMessage
              id="pixi_wizard.complete.licenses"
              defaultMessage="{count, plural, one {{count} License} other {{count} Licenses}}"
              values={{ count: licenses }}
            />
          </RowLabel>
          <LicenseValue>
            {estimatePending ? (
              <Loader
                inline
                active
                size="small"
                data-testid="complete-licenses-price-loading"
              />
            ) : (
              <RowValue data-testid="complete-licenses-price">
                {price(displayTotalCents)}
              </RowValue>
            )}
            <RenewSubtext data-testid="complete-renewal">
              {isYearly ? (
                <FormattedMessage
                  id="pixi_wizard.complete.renewed_yearly"
                  defaultMessage="Renewed every year"
                />
              ) : (
                <FormattedMessage
                  id="pixi_wizard.complete.renewed_monthly"
                  defaultMessage="Renewed every month"
                />
              )}
            </RenewSubtext>
          </LicenseValue>
        </SummaryRow>

        {isFreeDevice && (
          <>
            <Divider />
            <SummaryRow>
              <FreeDeviceLabel data-testid="complete-pixi-line">
                <FormattedMessage
                  id="pixi_wizard.complete.pixi_line"
                  defaultMessage="{count} free {pixiLogo} {count, plural, one {device} other {devices}} on your way!"
                  values={{
                    count: licenses,
                    pixiLogo: <PixiInlineLogo src={darkPixiIcon} alt="PIXI" />,
                  }}
                />
              </FreeDeviceLabel>
              <RowValue data-testid="complete-pixi-amount">
                {price(0)}{" "}
                <PixiSavings>(-{price(deviceSavingsCents)})</PixiSavings>
              </RowValue>
            </SummaryRow>
          </>
        )}
      </SummaryCard>

      <CompleteButtonRow>
        <Button primary onClick={onComplete} data-testid="complete-button">
          <FormattedMessage
            id="pixi_wizard.complete.complete"
            defaultMessage="Complete"
          />
        </Button>
      </CompleteButtonRow>
    </CompleteLayout>
  );
};
